import { useEffect, useRef, useState } from "react";
import Burst from "@/components/Burst";

/**
 * THE MARK — drawn from the numbers the app draws it from (ExpertsApp ui/Mark.kt, MarkGeo,
 * make_marks_v2.py). Never by eye. (What each shape stands for lives in the docs, not on the site —
 * Dog 2026-10-27: "less is more".)
 *
 * The full mark (four shapes, hairline 12.288 on 1024) is used at every size we show. 2026-10-27:
 * the header used the 32 px cut (circle + triangle only) and Dog saw the centre circle missing —
 * small sizes now draw the full mark with a heavier stroke instead.
 */
const G = {
  stroke: 12.288,
  circle: 449.536,
  tri: "512,80.896 138.653,727.552 885.347,727.552",
  sq: { x: 347.6717, y: 398.8954, s: 328.6566 },
  inner: { cy: 563.2237, r: 152.0403 },
};

function Shapes({ anim }) {
  return (
    <>
      <circle cx="512" cy="512" r={G.circle} pathLength="1" style={anim?.(0)} />
      <polygon points={G.tri} pathLength="1" style={anim?.(1)} />
      <rect x={G.sq.x} y={G.sq.y} width={G.sq.s} height={G.sq.s} pathLength="1" style={anim?.(2)} />
      <circle cx="512" cy={G.inner.cy} r={G.inner.r} pathLength="1" style={anim?.(3)} />
    </>
  );
}

export default function Mark({ size = 28, color = "currentColor", draw = false, strokeBoost, className = "", title }) {
  // a hairline that stays visible at small sizes: about 1.1 px at the smallest
  const boost = strokeBoost ?? Math.max(1, (1.1 * 1024) / (size * G.stroke));
  const anim = draw ? (i) => ({
    strokeDasharray: 1, strokeDashoffset: 1,
    animation: `ex-draw 1.15s ${0.15 + i * 0.32}s cubic-bezier(.6,0,.2,1) forwards`,
  }) : undefined;
  return (
    <svg
      viewBox="0 0 1024 1024" width={size} height={size} className={className}
      fill="none" stroke={color} strokeWidth={G.stroke * boost} strokeLinejoin="miter"
      role={title ? "img" : undefined} aria-label={title} aria-hidden={title ? undefined : true}
    >
      <Shapes anim={anim} />
    </svg>
  );
}

/** Mark + wordmark, as the app's top bar shows it — the full mark, centre circle included. */
export function Lockup({ size = 24 }) {
  return (
    <span className="inline-flex items-center gap-3">
      <Mark size={size} />
      <span className="ex-wordmark">EXPERTS</span>
    </span>
  );
}

/**
 * THE OPENING (Dog 2026-10-27: "it should be the very first thing you see. the lines trace out the
 * icon then they spin/oscillate like they do in the mobile app and explode into the landing page").
 *
 * The app's finish (Mark.kt MarkCelebrate + Burst.kt), for the web:
 *   1. the four shapes trace themselves in, one after another;
 *   2. each starts turning on its own axis half a second after the last — the inner circle on X,
 *      the square on Y, the triangle in place, the outer circle on X — gentle, then racing, with
 *      motion-blur ghosts once it is fast;
 *   3. the burst goes off from its centre and the page shows through as the backdrop fades.
 * Once per visit (sessionStorage), never for people who ask for reduced motion, tap to skip.
 */
const SEEN = "experts_intro_seen";
const DRAW_END = 1.55;     // s — the trace is done
const SPEED = 1.3;         // the app's spin, a little quicker on a web page
const BURST_AT = 3.05;     // spin-seconds — the app's hand-off to the burst
const REVEAL = 0.9;        // s — the backdrop fades out under the burst
const BURST_SECONDS = 5.8;

function turnsAt(time, i) {
  const dt = time - i * 0.5;
  if (dt <= 0) return 0;
  const fast = Math.max(0, time - 2.0);
  return dt * 0.8 + fast * fast * fast * 1.6;      // gentle, then racing (Mark.kt turnsAt)
}

function spinTransforms(time) {
  const c = (turns) => Math.cos(turns * 2 * Math.PI);
  const sqc = { x: G.sq.x + G.sq.s / 2, y: G.sq.y + G.sq.s / 2 };
  const about = (px, py, sx, sy) => `translate(${px} ${py}) scale(${sx} ${sy}) translate(${-px} ${-py})`;
  return [
    about(512, 512, 1, c(turnsAt(time, 3))),                   // outer circle — on X
    `rotate(${turnsAt(time, 2) * 360} 512 512)`,               // triangle — in place
    about(sqc.x, sqc.y, c(turnsAt(time, 1)), 1),               // square — on Y
    about(512, G.inner.cy, 1, c(turnsAt(time, 0))),            // inner circle — on X
  ];
}

export function IntroSplash() {
  const [show, setShow] = useState(() => {
    try {
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
      return sessionStorage.getItem(SEEN) !== "1";
    } catch (_) { return true; }
  });
  const [stage, setStage] = useState("draw");      // draw · spin · burst · gone
  const [burst, setBurst] = useState(0);
  const groups = useRef([]);
  const ghosts = useRef([]);
  const markRef = useRef(null);
  const start = useRef(0);

  useEffect(() => {
    if (!show) return undefined;
    try { sessionStorage.setItem(SEEN, "1"); } catch (_) { /* private mode */ }
    document.body.style.overflow = "hidden";
    let raf = 0;
    start.current = performance.now();
    const frame = (now) => {
      const t = Math.max(0, (now - start.current) / 1000);
      const ts = t > DRAW_END ? (t - DRAW_END) * SPEED : 0;
      if (t > DRAW_END) {
        const tr = spinTransforms(ts);
        groups.current.forEach((g, i) => g && g.setAttribute("transform", tr[i]));
        const blur = Math.min(1, Math.max(0, (ts - 2.2) / 1.0));
        ghosts.current.forEach((gg, k) => {
          if (!gg) return;
          const back = spinTransforms(ts - (k + 1) * 0.012);
          [...gg.children].forEach((g, i) => g.setAttribute("transform", back[i]));
          gg.style.opacity = String(0.22 * blur);
        });
        const fade = Math.min(1, Math.max(0, (ts - 3.1) / 0.5));
        if (markRef.current) {
          markRef.current.style.opacity = String(1 - fade);
          markRef.current.style.transform = `scale(${1 + fade * 0.35})`;
        }
      }
      if (ts >= BURST_AT && stage !== "burst") { setStage("burst"); setBurst(1); }
      if (ts < BURST_AT + 0.6) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (stage !== "burst") return undefined;
    document.body.style.overflow = "";
    const t = setTimeout(() => setStage("gone"), BURST_SECONDS * 1000);
    return () => clearTimeout(t);
  }, [stage]);

  if (!show || stage === "gone") return null;
  const revealing = stage === "burst";
  const skip = () => { document.body.style.overflow = ""; setStage("gone"); };
  const drawAnim = (i) => ({
    strokeDasharray: 1, strokeDashoffset: 1,
    animation: `ex-draw 0.95s ${0.1 + i * 0.2}s cubic-bezier(.6,0,.2,1) forwards`,
  });

  return (
    <div
      className="fixed inset-0 z-[70]"
      style={{ pointerEvents: revealing ? "none" : "auto" }}
      onClick={revealing ? undefined : skip}
      aria-hidden
      data-testid="intro"
    >
      <div
        className="absolute inset-0"
        style={{ background: "var(--ex-bg)", opacity: revealing ? 0 : 1, transition: `opacity ${REVEAL}s ease` }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div ref={markRef} style={{ width: "min(62vw, 340px)", aspectRatio: "1 / 1", transition: "none" }}>
          <svg viewBox="0 0 1024 1024" width="100%" height="100%" fill="none" stroke="#f2f2f2"
               strokeWidth={G.stroke * 1.7} strokeLinejoin="miter" style={{ overflow: "visible" }}>
            {[0, 1, 2, 3].map((k) => (
              <g key={`ghost${k}`} ref={(el) => { ghosts.current[k] = el; }} style={{ opacity: 0 }}>
                <g><circle cx="512" cy="512" r={G.circle} /></g>
                <g><polygon points={G.tri} /></g>
                <g><rect x={G.sq.x} y={G.sq.y} width={G.sq.s} height={G.sq.s} /></g>
                <g><circle cx="512" cy={G.inner.cy} r={G.inner.r} /></g>
              </g>
            ))}
            <g ref={(el) => { groups.current[0] = el; }}><circle cx="512" cy="512" r={G.circle} pathLength="1" style={drawAnim(0)} /></g>
            <g ref={(el) => { groups.current[1] = el; }}><polygon points={G.tri} pathLength="1" style={drawAnim(1)} /></g>
            <g ref={(el) => { groups.current[2] = el; }}><rect x={G.sq.x} y={G.sq.y} width={G.sq.s} height={G.sq.s} pathLength="1" style={drawAnim(2)} /></g>
            <g ref={(el) => { groups.current[3] = el; }}><circle cx="512" cy={G.inner.cy} r={G.inner.r} pathLength="1" style={drawAnim(3)} /></g>
          </svg>
        </div>
      </div>
      <div className="absolute inset-0 pointer-events-none"><Burst play={burst} /></div>
    </div>
  );
}
